/**
 * AEOQuestionsPanel Component
 *
 * Panel for managing Q&A extracted from AEO content.
 * Shows questions with approve/reject/sync capabilities.
 */

import React, { useState, useEffect } from 'react';
import {
  HelpCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  ExternalLink,
  ArrowRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { authFetch } from '@/utils/auth-fetch';
import { cn } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';

interface AEOQuestion {
  id: string;
  question: string;
  answer: string;
  source_section: string;
  status: 'draft' | 'approved' | 'rejected' | 'synced';
  category_path: string;
  created_at: string;
  synced_at?: string;
  question_wheel_id?: string;
}

interface AEOQuestionsPanelProps {
  siteId: string;
  contentId?: string;
  categoryPath?: string;
  onSync?: (wheelId: string) => void;
  className?: string;
}

export function AEOQuestionsPanel({
  siteId,
  contentId,
  categoryPath,
  onSync,
  className,
}: AEOQuestionsPanelProps) {
  const { toast } = useToast();
  const [questions, setQuestions] = useState<AEOQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadQuestions();
  }, [siteId, categoryPath]);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      let url = `/aeo-questions/sites/${siteId}`;
      if (categoryPath) {
        url += `?categoryPath=${encodeURIComponent(categoryPath)}`;
      }

      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        setQuestions(data.data || []);
      }
    } catch (error) {
      console.error('Error loading questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const draftIds = questions
        .filter(q => q.status === 'draft')
        .map(q => q.id);
      setSelectedIds(new Set(draftIds));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;

    setActionLoading(true);
    try {
      const response = await authFetch('/aeo-questions/bulk-approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questionIds: Array.from(selectedIds) }),
      });

      if (response.ok) {
        const data = await response.json();
        toast({
          title: 'Questions Approved',
          description: `${data.data.approvedCount} questions approved for sync`,
        });
        setSelectedIds(new Set());
        loadQuestions();
      }
    } catch (error) {
      console.error('Error approving questions:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve questions',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async (questionId: string) => {
    setActionLoading(true);
    try {
      const response = await authFetch(`/aeo-questions/${questionId}/approve`, {
        method: 'PUT',
      });

      if (response.ok) {
        toast({ title: 'Question Approved' });
        loadQuestions();
      }
    } catch (error) {
      console.error('Error approving question:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (questionId: string) => {
    setActionLoading(true);
    try {
      const response = await authFetch(`/aeo-questions/${questionId}/reject`, {
        method: 'PUT',
      });

      if (response.ok) {
        toast({ title: 'Question Rejected' });
        loadQuestions();
      }
    } catch (error) {
      console.error('Error rejecting question:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSyncToWheel = async () => {
    if (!categoryPath) {
      toast({
        title: 'Error',
        description: 'Category path is required for sync',
        variant: 'destructive',
      });
      return;
    }

    setActionLoading(true);
    try {
      const response = await authFetch(`/aeo-questions/sync/${siteId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categoryPath }),
      });

      if (response.ok) {
        const data = await response.json();
        toast({
          title: 'Synced to Question Wheel',
          description: `${data.data.syncedCount} questions synced`,
        });
        loadQuestions();
        if (data.data.wheelId && onSync) {
          onSync(data.data.wheelId);
        }
      }
    } catch (error: any) {
      console.error('Error syncing to wheel:', error);
      toast({
        title: 'Sync Failed',
        description: error.message || 'Failed to sync questions',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const statusCounts = {
    draft: questions.filter(q => q.status === 'draft').length,
    approved: questions.filter(q => q.status === 'approved').length,
    synced: questions.filter(q => q.status === 'synced').length,
    rejected: questions.filter(q => q.status === 'rejected').length,
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <Badge variant="secondary">Draft</Badge>;
      case 'approved':
        return <Badge variant="default" className="bg-amber-500">Ready</Badge>;
      case 'synced':
        return <Badge variant="default" className="bg-green-600">Synced</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">Loading questions...</div>
        </CardContent>
      </Card>
    );
  }

  if (questions.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">
            No questions extracted yet. Save the AEO content to extract FAQs.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <HelpCircle className="h-4 w-4" />
              AEO Questions
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              {questions.length} questions extracted from FAQs
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-500">{statusCounts.draft} draft</span>
            <span className="text-amber-500">{statusCounts.approved} ready</span>
            <span className="text-green-600">{statusCounts.synced} synced</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Action Bar */}
        <div className="flex items-center justify-between mb-4 pb-3 border-b">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={selectedIds.size > 0 && selectedIds.size === statusCounts.draft}
              onCheckedChange={handleSelectAll}
              disabled={statusCounts.draft === 0}
            />
            <span className="text-sm text-muted-foreground">
              {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all draft'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleBulkApprove}
                disabled={actionLoading}
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Approve Selected
              </Button>
            )}
            {statusCounts.approved > 0 && (
              <Button
                size="sm"
                onClick={handleSyncToWheel}
                disabled={actionLoading}
              >
                <ArrowRight className="h-4 w-4 mr-1" />
                Sync to Wheel ({statusCounts.approved})
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={loadQuestions}
              disabled={loading}
            >
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            </Button>
          </div>
        </div>

        {/* Questions List */}
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {questions.map((q) => (
            <div
              key={q.id}
              className={cn(
                'p-3 rounded-lg border',
                q.status === 'synced' && 'bg-green-50 border-green-200',
                q.status === 'approved' && 'bg-amber-50 border-amber-200',
                q.status === 'rejected' && 'bg-red-50 border-red-200 opacity-60',
                q.status === 'draft' && 'bg-muted/30'
              )}
            >
              <div className="flex items-start gap-3">
                {q.status === 'draft' && (
                  <Checkbox
                    checked={selectedIds.has(q.id)}
                    onCheckedChange={(checked) => handleSelect(q.id, checked as boolean)}
                    className="mt-1"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {getStatusBadge(q.status)}
                    <span className="text-xs text-muted-foreground">
                      {q.source_section}
                    </span>
                  </div>
                  <p className="font-medium text-sm">{q.question}</p>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                    {q.answer}
                  </p>
                </div>
                {q.status === 'draft' && (
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-green-600 hover:bg-green-100"
                      onClick={() => handleApprove(q.id)}
                      disabled={actionLoading}
                    >
                      <CheckCircle className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-red-600 hover:bg-red-100"
                      onClick={() => handleReject(q.id)}
                      disabled={actionLoading}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                {q.status === 'synced' && q.question_wheel_id && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 text-xs"
                    asChild
                  >
                    <a href={`/question-wheels/${q.question_wheel_id}/debug`}>
                      <ExternalLink className="h-3 w-3 mr-1" />
                      View Wheel
                    </a>
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default AEOQuestionsPanel;
